import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { apiClient, endpoints, getJWT } from '../utils';
import _ from 'lodash';

interface AdminAppContextType {
	intercomBadge?: number;
	dealChatsBadge?: number;
	webSocket?: WebSocket | null;
}

const defaultContextValue: AdminAppContextType = {
	intercomBadge: 0,
	dealChatsBadge: 0,
	webSocket: null,
};

const AdminAppContext = createContext<AdminAppContextType>(defaultContextValue);

export const AdminAppProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
	const [intercomChats, setIntercomChats] = useState<number>(0);
	const [dealChats, setDealChats] = useState<number>(0);
	const [webSocket, setWebSocket] = useState<WebSocket | null>(null);

	useEffect(() => {
		// Fetch initial data
		loadIntercomChats();
		loadDealChats();

		// Establish WebSocket connection
		getJWT().then((token) => {
			const ws = new WebSocket(`${endpoints.notificationWebSocket}?token=${token}`);
			setWebSocket(ws);

			ws.onmessage = (event) => {
				try {
					const receivedMessage = JSON.parse(event.data);

					switch (receivedMessage.type) {
						case 'Intercom':
							if (receivedMessage.content) {
								setIntercomChats(_.toNumber(receivedMessage.content));
							} else {
								loadIntercomChats();
							}
							break;
						case 'Dealchats':
							if (receivedMessage.content) {
								setDealChats(_.toNumber(receivedMessage.content));
							} else {
								loadDealChats();
							}
							break;
					}
				} catch (error) {
					console.error('(AdminAppContext) Error parsing WebSocket message:', error);
				}
			};

			return () => {
				if (ws) {
					ws.close();
				}
			};
		});
	}, []);

	const loadIntercomChats = async () => {
		const res = await apiClient.post(endpoints.getChats, {
			page: 1,
			pageSize: 20,
			isNew: true,
		});
		const data = res.data.data;

		if (data) {
			setIntercomChats(data.length);
		}
	};

	const loadDealChats = async () => {
		const res = await apiClient.post(endpoints.getDealchats, {
			page: 1,
			pageSize: 30,
			isNew: true,
		});
		const data = res.data.data;

		if (data) {
			setDealChats(data.length);
		}
	};

	return (
		<AdminAppContext.Provider
			value={{
				intercomBadge: intercomChats,
				dealChatsBadge: dealChats,
				webSocket: webSocket,
			}}
		>
			{children}
		</AdminAppContext.Provider>
	);
};

export const useAdminAppContext = () => useContext(AdminAppContext);
